import React, { useEffect, useMemo, useState } from "react";
import { api, formatTRY, formatDate } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  CalendarClock,
  Wrench,
  ShieldCheck,
  Package,
  Store,
  Wallet,
  Landmark,
  TrendingUp,
  TrendingDown,
  FileDown,
  Scale,
} from "lucide-react";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const inMonth = (iso, key) => {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    return monthKey(d) === key;
  } catch {
    return false;
  }
};

const gross = (j) => (j.price || 0) * (1 + (j.vat_rate || 0));

const Section = ({ title, icon: Icon, children, right = null, testId }) => (
  <section data-testid={testId} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
    <div className="flex items-center justify-between gap-3 p-4 border-b border-slate-100 bg-slate-50">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-700" />
        <h3 className="font-display text-sm font-bold text-slate-900">{title}</h3>
      </div>
      {right}
    </div>
    <div className="divide-y divide-slate-100">{children}</div>
  </section>
);

const Row = ({ left, right, muted = false, danger = false }) => (
  <div className="flex items-start justify-between gap-3 px-4 py-2.5 hover:bg-slate-50/50">
    <div className={`text-sm ${muted ? "text-slate-500" : "text-slate-700"}`}>{left}</div>
    <div className={`text-sm font-mono-num font-semibold shrink-0 ${danger ? "text-rose-700" : "text-slate-900"}`}>{right}</div>
  </div>
);

const Empty = ({ label }) => (
  <div className="px-4 py-6 text-center text-sm text-slate-400">{label}</div>
);

export default function Periods() {
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => monthKey(new Date()));
  const [jobs, setJobs] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [shopExp, setShopExp] = useState([]);
  const [loans, setLoans] = useState([]);
  const [salaries, setSalaries] = useState([]);

  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = monthKey(d);
      opts.push({ value: key, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` });
    }
    return opts;
  }, []);

  const label = monthOptions.find((o) => o.value === selectedMonth)?.label || selectedMonth;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [j, i, s, sh, l, sa] = await Promise.all([
          api.get("/jobs"),
          api.get("/insurance"),
          api.get("/supplies"),
          api.get("/shop-expenses"),
          api.get("/loans"),
          api.get("/salaries").catch(() => ({ data: [] })),
        ]);
        setJobs(j.data || []);
        setInsurance(i.data || []);
        setSupplies(s.data || []);
        setShopExp(sh.data || []);
        setLoans(l.data || []);
        setSalaries(sa.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Ay bazında agregasyonlar (sadece o ay içinde gerçekleşen hareketler)
  const data = useMemo(() => {
    const key = selectedMonth;

    // İş kayıtları: o ayda açılan + o ayda tahsil edilen
    const jobsOpened = jobs.filter((j) => inMonth(j.date, key));
    const jobsCollected = jobs.filter(
      (j) => inMonth(j.collected_date, key) && (j.status === "Tahsil Edildi" || j.status === "Kısmi Ödeme")
    );
    const jobsDelivered = jobs.filter(
      (j) => inMonth(j.delivered_date, key) && j.status === "Teslim Edildi"
    );
    const jobsIncome = jobsCollected.reduce((sum, j) => {
      if (j.status === "Tahsil Edildi") return sum + gross(j);
      if (j.status === "Kısmi Ödeme") return sum + (j.paid_amount || 0);
      return sum;
    }, 0);

    // Sigorta: o ayda açılan + o ayda tahsil edilen
    const insOpened = insurance.filter((c) => inMonth(c.date, key));
    const insCollected = insurance.filter((c) => inMonth(c.collected_date, key) && c.status === "Tahsil Edildi");
    const insIncome = insCollected.reduce((sum, c) => sum + (c.collected_amount || 0), 0);

    // Malzeme: paid_date o ayda
    const suppliesPaid = supplies.filter((s) => inMonth(s.paid_date, key));
    const supplyExpense = suppliesPaid.reduce((sum, s) => sum + (s.paid_amount || 0), 0);
    // Kategori ayrımı
    const normalizeCat = (c) => {
      const v = (c || "").toLowerCase();
      if (v.includes("boyahane")) return "Boyahane Malzeme";
      if (v.includes("kaporta")) return "Kaporta Malzeme";
      return "Parça";
    };

    // Dükkan: paid_date o ayda
    const shopPaid = shopExp.filter((s) => inMonth(s.paid_date, key));
    const shopExpense = shopPaid.reduce((sum, s) => sum + (s.paid_amount || 0), 0);

    // Alınan borç: paid_date o ayda (geri ödeme)
    const loansPaid = loans.filter((l) => inMonth(l.paid_date, key) && l.direction === "borrowed");
    const loanExpense = loansPaid.reduce((sum, l) => sum + (l.paid_amount || 0), 0);

    // Verilen borç tahsilat
    const lentCollected = loans.filter((l) => inMonth(l.paid_date, key) && l.direction === "lent");
    const lentIncome = lentCollected.reduce((sum, l) => sum + (l.paid_amount || 0), 0);

    // Maaş ödemeleri
    const salPaid = salaries.filter((s) => inMonth(s.date || s.paid_date, key));
    const salaryExpense = salPaid.reduce((sum, s) => sum + (s.amount || 0), 0);

    const totalIncome = jobsIncome + insIncome + lentIncome;
    const totalExpense = supplyExpense + shopExpense + loanExpense + salaryExpense;

    return {
      jobsOpened,
      jobsCollected,
      jobsDelivered,
      jobsIncome,
      insOpened,
      insCollected,
      insIncome,
      suppliesPaid,
      supplyExpense,
      shopPaid,
      shopExpense,
      loansPaid,
      loanExpense,
      lentCollected,
      lentIncome,
      salPaid,
      salaryExpense,
      totalIncome,
      totalExpense,
      netProfit: totalIncome - totalExpense,
      normalizeCat,
    };
  }, [selectedMonth, jobs, insurance, supplies, shopExp, loans, salaries]);

  const [y, m] = selectedMonth.split("-");
  const pdfHref = `/print/monthly-report?month=${parseInt(m, 10)}&year=${y}`;

  if (loading) {
    return <div data-testid="periods-loading" className="py-12 text-center text-slate-500">Yükleniyor…</div>;
  }

  return (
    <div data-testid="periods-page" className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Geçmiş Dönemler</p>
          <p className="text-sm text-slate-700">
            Seçili ay: <span data-testid="active-month" className="font-semibold text-slate-900">{label}</span> · Sadece o ay içinde yapılan hareketler
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger data-testid="period-month-select" className="w-[200px] bg-white">
              <SelectValue placeholder="Ay seç" />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`period-month-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <a
            href={pdfHref}
            target="_blank"
            rel="noreferrer"
            data-testid="period-pdf-btn"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm transition-colors"
          >
            <FileDown className="w-4 h-4" /> A4 PDF Yazdır
          </a>
        </div>
      </div>

      {/* HERO — AYLIK NET BAKİYE */}
      <section
        data-testid="period-hero"
        className="rounded-xl p-6 sm:p-8 border bg-gradient-to-br from-slate-900 to-slate-800 border-slate-800 text-white"
      >
        <div className="flex items-center gap-2">
          <Scale className="w-4 h-4 text-slate-300" />
          <p className="text-[11px] uppercase tracking-widest text-slate-300 font-semibold">
            {label} · Net Bakiye
          </p>
        </div>
        <p
          data-testid="period-net-value"
          className={`font-display text-3xl sm:text-5xl font-bold mt-2 font-mono-num ${data.netProfit < 0 ? "text-rose-300" : "text-emerald-300"}`}
        >
          {formatTRY(data.netProfit)}
        </p>
        <p className="text-xs sm:text-sm mt-2 text-slate-300">
          Gelir {formatTRY(data.totalIncome)} − Gider {formatTRY(data.totalExpense)} · Sadece bu ayda gerçekleşen hareketler
        </p>
      </section>

      {/* GELİR & GİDER ÖZET */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <section data-testid="period-income-card" className="bg-white border border-emerald-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-emerald-700" />
            <p className="text-[11px] uppercase tracking-widest font-semibold text-emerald-700">Gelir</p>
          </div>
          <p className="font-display text-2xl font-bold font-mono-num text-emerald-700" data-testid="period-income-total">
            {formatTRY(data.totalIncome)}
          </p>
          <div className="mt-3 space-y-1 text-xs text-slate-600">
            <div className="flex justify-between"><span>İş tahsilatları ({data.jobsCollected.length})</span><span className="font-mono-num">{formatTRY(data.jobsIncome)}</span></div>
            <div className="flex justify-between"><span>Sigorta tahsilatı ({data.insCollected.length})</span><span className="font-mono-num">{formatTRY(data.insIncome)}</span></div>
            <div className="flex justify-between"><span>Verilen borç geri alındı ({data.lentCollected.length})</span><span className="font-mono-num">{formatTRY(data.lentIncome)}</span></div>
          </div>
        </section>
        <section data-testid="period-expense-card" className="bg-white border border-rose-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown className="w-4 h-4 text-rose-700" />
            <p className="text-[11px] uppercase tracking-widest font-semibold text-rose-700">Gider</p>
          </div>
          <p className="font-display text-2xl font-bold font-mono-num text-rose-700" data-testid="period-expense-total">
            {formatTRY(data.totalExpense)}
          </p>
          <div className="mt-3 space-y-1 text-xs text-slate-600">
            <div className="flex justify-between"><span>Maaş ({data.salPaid.length})</span><span className="font-mono-num">{formatTRY(data.salaryExpense)}</span></div>
            <div className="flex justify-between"><span>Malzeme ({data.suppliesPaid.length})</span><span className="font-mono-num">{formatTRY(data.supplyExpense)}</span></div>
            <div className="flex justify-between"><span>Dükkan ({data.shopPaid.length})</span><span className="font-mono-num">{formatTRY(data.shopExpense)}</span></div>
            <div className="flex justify-between"><span>Alınan borç ödeme ({data.loansPaid.length})</span><span className="font-mono-num">{formatTRY(data.loanExpense)}</span></div>
          </div>
        </section>
      </div>

      {/* İş Kayıtları: Yapılan + Tahsil edilen */}
      <Section testId="period-jobs" title={`İş Kayıtları (${data.jobsOpened.length} açılan · ${data.jobsCollected.length} tahsilat)`} icon={Wrench}>
        {data.jobsOpened.length === 0 && data.jobsCollected.length === 0 && <Empty label="Bu ayda iş kaydı yok." />}
        {data.jobsOpened.map((j) => (
          <Row
            key={`opened-${j.id}`}
            left={<><span className="font-semibold">{formatDate(j.date)}</span> · {j.customer_name || "-"} · <span className="text-slate-500">{j.plate || j.car_model || ""}</span> <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{j.status}</span></>}
            right={formatTRY(gross(j))}
          />
        ))}
        {data.jobsCollected.filter((j) => !data.jobsOpened.find((o) => o.id === j.id)).map((j) => (
          <Row
            key={`col-${j.id}`}
            left={<><span className="font-semibold">{formatDate(j.collected_date)}</span> · {j.customer_name || "-"} <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Tahsilat</span></>}
            right={formatTRY(j.status === "Kısmi Ödeme" ? j.paid_amount : gross(j))}
          />
        ))}
      </Section>

      {/* Sigorta */}
      <Section testId="period-insurance" title={`Sigorta Dosyaları (${data.insOpened.length} açılan · ${data.insCollected.length} tahsilat)`} icon={ShieldCheck}>
        {data.insOpened.length === 0 && data.insCollected.length === 0 && <Empty label="Bu ayda sigorta hareketi yok." />}
        {data.insOpened.map((c) => (
          <Row
            key={`insopened-${c.id}`}
            left={<><span className="font-semibold">{formatDate(c.date)}</span> · {c.company_name || c.customer_name || "-"} <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{c.status}</span></>}
            right={formatTRY(c.approved_amount || c.damage_amount || 0)}
          />
        ))}
        {data.insCollected.filter((c) => !data.insOpened.find((o) => o.id === c.id)).map((c) => (
          <Row
            key={`inscol-${c.id}`}
            left={<><span className="font-semibold">{formatDate(c.collected_date)}</span> · {c.company_name || "-"} <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Tahsilat</span></>}
            right={formatTRY(c.collected_amount || 0)}
          />
        ))}
      </Section>

      {/* Malzeme Giderleri */}
      <Section testId="period-supplies" title={`Malzeme Giderleri (${data.suppliesPaid.length})`} icon={Package}>
        {data.suppliesPaid.length === 0 && <Empty label="Bu ayda malzeme ödemesi yok." />}
        {data.suppliesPaid.map((s) => (
          <Row
            key={s.id}
            left={<><span className="font-semibold">{formatDate(s.paid_date)}</span> · {s.item_name} <span className="text-slate-500">· {data.normalizeCat(s.category)}</span></>}
            right={formatTRY(s.paid_amount || 0)}
            danger
          />
        ))}
      </Section>

      {/* Dükkan Giderleri */}
      <Section testId="period-shop" title={`Dükkan Giderleri (${data.shopPaid.length})`} icon={Store}>
        {data.shopPaid.length === 0 && <Empty label="Bu ayda dükkan ödemesi yok." />}
        {data.shopPaid.map((s) => (
          <Row
            key={s.id}
            left={<><span className="font-semibold">{formatDate(s.paid_date)}</span> · {s.name || "-"}</>}
            right={formatTRY(s.paid_amount || 0)}
            danger
          />
        ))}
      </Section>

      {/* Maaş Ödemeleri */}
      <Section testId="period-salaries" title={`Maaş Ödemeleri (${data.salPaid.length})`} icon={Wallet}>
        {data.salPaid.length === 0 && <Empty label="Bu ayda maaş ödemesi yok." />}
        {data.salPaid.map((s, idx) => (
          <Row
            key={s.id || `sal-${idx}`}
            left={<><span className="font-semibold">{formatDate(s.date || s.paid_date)}</span> · {s.employee_name || "Çalışan"} {s.week_start ? <span className="text-slate-500">· Hafta {formatDate(s.week_start)}</span> : null}</>}
            right={formatTRY(s.amount || 0)}
            danger
          />
        ))}
      </Section>

      {/* Finans — alınan borç ödeme + verilen borç geri alma */}
      <Section testId="period-finans" title={`Finans Hareketleri (${data.loansPaid.length + data.lentCollected.length})`} icon={Landmark}>
        {data.loansPaid.length === 0 && data.lentCollected.length === 0 && <Empty label="Bu ayda finans hareketi yok." />}
        {data.loansPaid.map((l) => (
          <Row
            key={`b-${l.id}`}
            left={<><span className="font-semibold">{formatDate(l.paid_date)}</span> · {l.counterparty || "Borç"} <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Alınan borç ödeme</span></>}
            right={formatTRY(l.paid_amount || 0)}
            danger
          />
        ))}
        {data.lentCollected.map((l) => (
          <Row
            key={`l-${l.id}`}
            left={<><span className="font-semibold">{formatDate(l.paid_date)}</span> · {l.counterparty || "Alacak"} <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Verilen borç geri alma</span></>}
            right={formatTRY(l.paid_amount || 0)}
          />
        ))}
      </Section>
    </div>
  );
}
